'use client';

import { useEffect } from 'react';

import { useLazyGetWorkspaceMembersQuery } from '@/features/workspaces/api/workspacesApi';
import { Avatar, SmartInput } from '@web/ui';

import styles from './WorkspaceMemberSelectField.module.scss';

import type { WorkspaceMemberItem } from '@/features/workspaces/types';

type WorkspaceMemberSelectFieldProps = {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  emptyLabel?: string;
  showPreview?: boolean;
  selectedLabel?: string;
  selectedAvatarSrc?: string;
  size?: 'small' | 'medium';
};

const EMPTY_OPTION_VALUE = '__workspace_member_empty__';

const toDisplayName = (member: WorkspaceMemberItem): string => {
  const firstName = member.user.first_name ?? '';
  const lastName = member.user.last_name ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return member.user.email ?? 'Unknown member';
};

const toAvatarSrc = (member: WorkspaceMemberItem): string | undefined => {
  const rawUser = member.user as WorkspaceMemberItem['user'] & {
    avatar_url?: string | null;
    avatar?: string | null;
  };

  return rawUser.avatar_url ?? rawUser.avatar ?? undefined;
};

export function WorkspaceMemberSelectField({
  name,
  label,
  value,
  onChange,
  error,
  disabled = false,
  emptyLabel = 'Select member',
  showPreview = true,
  selectedLabel,
  selectedAvatarSrc,
  size = 'small',
}: WorkspaceMemberSelectFieldProps) {
  const [getWorkspaceMembers, { data, isFetching }] =
    useLazyGetWorkspaceMembersQuery();

  useEffect(() => {
    void getWorkspaceMembers(
      {
        status: 'active',
        page_size: 100,
      },
      true,
    );
  }, [getWorkspaceMembers]);

  const members = data?.data?.results ?? [];
  const selectedMember = members.find((member) => member.user.id === value);
  const selectedMemberLabel = selectedMember
    ? toDisplayName(selectedMember)
    : selectedLabel;
  const selectedMemberAvatar = selectedMember
    ? toAvatarSrc(selectedMember)
    : selectedAvatarSrc;
  const selectValue = value === '' ? EMPTY_OPTION_VALUE : value;
  let options = [
    { value: EMPTY_OPTION_VALUE, label: emptyLabel },
    ...members.map((member) => ({
      value: member.user.id,
      label: toDisplayName(member),
    })),
  ];

  if (
    value &&
    selectedLabel &&
    !options.some((option) => option.value === value)
  ) {
    options = [...options, { value, label: selectedLabel }];
  }

  return (
    <div className={styles.memberSelectField}>
      <SmartInput
        type="select"
        size={size}
        name={name}
        label={label}
        value={selectValue}
        options={options}
        error={error}
        readOnly={disabled || isFetching}
        onChange={(nextValue) => {
          const next = String(nextValue);
          onChange(next === EMPTY_OPTION_VALUE ? '' : next);
        }}
      />

      {showPreview && selectedMemberLabel ? (
        <span className={styles.memberPreview}>
          <Avatar
            name={selectedMemberLabel}
            src={selectedMemberAvatar}
            size="small"
          />
          {selectedMemberLabel}
        </span>
      ) : null}
    </div>
  );
}
